import { validateTokenForProvider } from '@/libraries/api/auth'
import {
  loadDomainsAndCameras,
  mappedDataFromAPIResponse,
} from '@/libraries/api/domains'
import {
  loadFacesByDomainUUID,
  updateLastSeen,
} from '@/libraries/api/faces'
import * as flags from '@/libraries/util/flags'
import { fakeIentityMatchingGenderOfFace } from '@/libraries/util/demoData'
import { Service, Operation } from '@/libraries/permissions'

// A handler to cancel timeouts on logout.
if (!window.owal) {
  window.owal = {}
}

window.owal.timeouts = {
  _timeouts: [],
  /**
   * Stores a reference to a scheduled timeout.
   *
   * @param {any} t The cancel token for the timeouts.
   */
  add (t) {
    this._timeouts.push(t)
  },
  /**
   * Clears the timeouts and then removes all tokens.
   */
  clear () {
    for (const t of this._timeouts) {
      window.cancelAnimationFrame(t)
    }
    this._timeouts = []
  },
}

export default {
  validateJWT: (context, user, callback) => {
    // console.log(`user: ${JSON.stringify(user.token, null, 2)}`)
    return validateTokenForProvider('google', user.token).then(value => {
      context.commit('finishLogin', value)
    })
  },
  loginError: (context, error) => {
    // TODO: Handle this appropriately. Depending on the kind of error, either
    // inform the user that they cancelled the operation, or some other kind of error.
    console.log(`OAuth error: ${JSON.stringify(error)}`)
  },
  logOut: context => {
    context.commit('clearAuthToken')
    context.commit('logout')

    // Clear adal.js on logout.
    for (var i = 0, len = localStorage.length; i < len; ++i) {
      const key = localStorage.key(i)

      if (key && key.startsWith('adal')) {
        localStorage.removeItem(key)
      }
    }

    window.owal.timeouts.clear()
  },
  refreshDataModel: ({ dispatch, commit, getters }) => {
    // // console.debug(`Checkpoint: actions.js -> refreshDataModel() called.')

    commit('incrementRequestCountForServiceNamed', 'data-model')
    /**
     * Thoughts:
     * 1. Do we want to do this in parallel?
     *    I think so, except for domains and entitlements, which come first.
     * 2. Do we want to get smart about only dispatching for currently visible UI?
     *    Probably, as this can speed up UI, but it needs to be done smartly.
     */

    const userUUID = 'me'
    // const userUUID = '64891e8f-9d6c-42c1-aa48-f5a103c4e612') // test joel
    // const userUUID = '8955e0fc-09a1-4a0d-a225-9a35a82c20e5') // test LeFrak
    // const userUUID = '8f5884aa-d106-4f1d-9317-e51d38e8a69e') // test tablet
    // const userUUID = '61722e46-abcb-4ee6-93ad-0a21c86296ba') // test director
    // const userUUID = '1d75342e-97d6-4c49-aa6c-63337c1e2c41') // test supervisor
    return new Promise((resolve, reject) => {
      const needToLoadDomains = !getters.domains.length
      if (needToLoadDomains) {
        dispatch('loadDomains')
          .then(_ =>
            dispatch('entitlements/loadPermissionsForUser', userUUID)
              .then(_ => {
                resolve();
                console.log('resolving loadpermissions for user')
              }
            )
          ) // Note: Pass function without calling it. https://stackoverflow.com/a/36657418
          .catch(error => reject(error))
      } else {
        resolve()
      }
    })
      .then(_ => commit('events/clearEvents'))
      .then(() => {
        commit('decrementRequestCountForServiceNamed', 'data-model')

        return Promise.all([
          dispatch('loadFrontendFaces'),
          dispatch('credentialMismatch/loadCredentialMismatchReport'),
          dispatch('events/loadEventsForDomain', { limit: 500 }),
          dispatch('elopementRisks/checkMostRecent'),
        ]).catch(error => {
          console.error(error)
          commit('decrementRequestCountForServiceNamed', 'data-model')
        })
      })
  },
  /**
   * Loads the domains and cameras data and builds a local map of the data.
   * @returns A promise which, when successful, resolves to the cache which was committed.
   *          When rejected, resolves to an error.
   */
  loadDomains: context => {
    // // console.debug(`Checkpoint: actions.js -> loadDomains() called.')
    // const domainUUID = context.getters.currentDomainUUID
    const ctx = context
    // console.debug('context: ', context)
    return new Promise((resolve, reject) => {
      loadDomainsAndCameras()
        .then(res => res.data)
        .then(data => {
          const domainUUID = ctx.getters.currentDomainUUID
          const cameraUUID = ctx.getters.currentCameraUUID
          // console.debug(`Checkpoint: actions.js -> loadDomainsAndCameras() "then handler" called.`)
          // console.debug(`                       -> current domain: ${domainUUID}.`)
          // console.debug(`                       -> current camera: ${cameraUUID}.`)
          const cache = mappedDataFromAPIResponse(data, domainUUID, cameraUUID)
          context.commit('cacheUserData', cache)
          resolve()
        })
        .catch(error => reject(error))
    })
  },
  /**
   * Checks entitlements, then loads the list of faces if access is allowed.
   *
   * @description
   *
   * If the current entitlements contain one of the following three entitlement pairs, the request is sent:
   * 1. `Service.FACES` and `Operation.FIND_ALL`
   * 2. `Service.FACES` and `Operation.FIND_RESIDENT`
   * 3. `Service.ALERTING` and `Operation.RECEIVE_ER_ALERTS.`
   *
   * If none of these entitlements are present, the request will fail.
   *
   * This method will also mask user names if demo mode is enabled.
   *
   * @returns A promise that resolves to `undefined` if successful.
   */
  loadFrontendFaces: context => {
    const canPerformOperationForService =
      context.getters['entitlements/canPerformOperationForService']
    const canViewElopementAlerts = canPerformOperationForService(
      Service.ALERTING,
      Operation.RECEIVE_ER_ALERTS
    )
    console.log('loadFrontendFaces performing entitlements check')
    const canViewFaces =
      canPerformOperationForService(Service.FACES, Operation.FIND_ALL) ||
      canPerformOperationForService(Service.FACES, Operation.FIND_RESIDENT)
    const shouldLoadFaces = canViewElopementAlerts || canViewFaces

    const domainUUID = context.getters.currentDomainUUID
    return new Promise((resolve, reject) => {
      if (!shouldLoadFaces) {
        return reject(Error(`Entitlements error: Cannot access Faces service.`))
      }
      context.commit('incrementRequestCountForServiceNamed', 'frontend-faces')

      loadFacesByDomainUUID({ domainUUID })
        .then(res => res.data)
        .then(data => {
          if (!data.ok) {
            console.error(`Store: Failed to refresh faces: ${data.err}`)
            return reject(Error(`${data.err}`))
          }

          if (!data.faces) {
            return reject(Error(`No faces.`))
          }

          let faces = data.faces

          /** If we hit this point, assume the user has limited access. */
          if (!canViewFaces) {
            faces = faces
              .filter(face => face.elopement_risk)
              .map(face => {
                return {
                  person_name: face.person_name,
                  frontend_face_uuid: face.frontend_face_uuid,
                }
              })
          } else if (flags.DemoDataForFaces.enabled) {
            /** Demo Modes - won't work for an ER only account. */
            faces = faces
              .filter(face => ['male', 'female'].includes(face.gender))
              .map(face => {
                const uuid = face.frontend_face_uuid
                let name = context.state.demoNames[uuid]

                if (!name) {
                  const demo = fakeIentityMatchingGenderOfFace(face)
                  name = `${demo.firstName} ${demo.lastName}`
                  context.commit('setDemoName', { uuid, name })
                }

                face.person_name = name

                return face
              })
          }

          const t = window.requestAnimationFrame(() => {
            if (!context || !context.state || !context.state.authToken) {
              console.log(`Can't refresh without auth token. Bailing instead.`)
            }

            /** Keep images in the correct order. */
            faces = faces.map(face => {
              if (!face.images) return face
              face.images = face.images.sort((a, b) =>
                a.orientation === 'front' ? -1 : 1
              )
              return face
            })

            faces.sort(
              (a, b) =>
                new Date(a.last_seen_datetime) > new Date(b.last_seen_datetime)
            )

            context.commit({ type: 'saveFrontendFaces', faces })
            return resolve()
          }, 1)
          window.owal.timeouts.add(t)
        })
        .catch(err => {
          console.log(`Store: Failed to refresh faces. Server error: ${err}`)
          reject(err)
        })
        .then(() => {
          context.commit(
            'decrementRequestCountForServiceNamed',
            'frontend-faces'
          )
          context.dispatch('updateLastSeen')
        })
    })
      .catch(err => {
        console.error(`Store: Failed to refresh faces: ${err}`)
      })
      .then(() => {
        context.commit('decrementRequestCountForServiceNamed', 'frontend-faces')
      })
  },
  updateLastSeen: context => {
    if (!context.state.authToken) {
      // console.log(`Can't refresh without auth token. Bailing instead.`)
      return
    }
    const domainUUID = context.getters.currentDomainUUID
    const canPerformOperation = context.getters['entitlements/canPerformOperationForService']
    console.log('updateLastSeen performing entitlements check')
    const canUpdateLastSeen = canPerformOperation(Service.FACES, Operation.FIND_ALL) || canPerformOperation(Service.FACES, Operation.FIND_RESIDENT)

    context.commit('incrementRequestCountForServiceNamed', 'last-seen')
    return new Promise((resolve, reject) => {
      if (!canUpdateLastSeen) {
        reject(Error(`Entitlements error: Can't update last seen.`))
      }
      updateLastSeen(domainUUID)
        .then(res => res.data)
        .then(data => {
          if (!data.ok) {
            console.log(`Store: Failed to refresh last seen.`)
            reject(Error(`${data.err}`))
          }

          if (!data.last_seen) {
            reject(Error(`There is no last seen data in the API request...`))
          }

          const t = window.requestAnimationFrame(() => {
            context.commit({ type: 'updateLastSeen', lastSeenData: data.last_seen })
            resolve()
          }, 1)
          window.owal.timeouts.add(t)
        })
        .catch(error => {
          console.log(`Error refreshing faces: ${error}`)
        })
        .then(() => {
          context.commit('decrementRequestCountForServiceNamed', 'last-seen')
        })
    })
      .catch(err => {
        console.debug(`Error refreshing last seen. ${err}`)
      })
      .then(() => {
        context.commit('decrementRequestCountForServiceNamed', 'last-seen')
      })
  },
  selectDomain: (context, domain) => {
    if (context.getters.currentDomain) {
      if (context.getters.currentDomain.uuid === domain.uuid) {
        console.debug(
          `Domain ${domain.description} is already the current domain.`
        )
        return
      }
    }
    return new Promise((resolve, reject) => {
      context.commit('makeDomainCurrentByUUID', domain.uuid)
      context.commit('events/clearFilters')
      context.dispatch('refreshDataModel').catch(error => {
        console.error('Error selecting domain: ', error)
      })
      resolve()
    })
  },
}
